import React from 'react';
import {
  EntityDescribedPropertyCategory,
  EntityDescribedPropertyFormat,
  JupiterTheme,
} from '@jupiterone/jupiter-web-toolkit';
import * as MockHook from '../../../../util/useMockHook';
import { createStyles, makeStyles } from '@material-ui/styles';
import { Tag as TagIcon } from 'react-feather';
import MoreIcon from 'react-feather/dist/icons/more-horizontal';
import {
  IconButton,
  Menu,
  Tooltip,
  Typography,
  MenuItem,
} from '@material-ui/core';
import { IndicatorChip } from '../../../..';
import { handleFindRelated, handleFindSimilar } from '../../../..';
import useEntityDrawerPropertyLogic, {
  EntityDrawerPropertyComponentProps,
} from './useEntityDrawerPropertyLogic';
import { reducePropertyType } from '../EntityDrawerPropertyTypes';

const useStyles = makeStyles((theme: JupiterTheme) => {
  return createStyles({
    row: {
      display: 'grid',
      gridTemplateColumns: 'auto 73px',
    },
    chip: {
      '&&': {
        fontSize: 11,
        height: 20,
        width: 80,
        margin: theme.spacing(0, 1),
      },
    },
    chipContainer: {
      display: 'flex',
      alignItems: 'center',
    },
    propertyName: {
      color: theme.palette.primary.dark,
    },
    propertyValue: {
      gridColumn: 'span 2',
      lineHeight: 1.35,
      marginBottom: theme.spacing(2),
      marginRight: theme.spacing(3),
      wordBreak: 'break-all',
      '& pre': {
        background: theme.palette.coolGrey[50],
        borderRadius: 3,
        fontFamily: `'Ubuntu Mono', monospace`,
        fontSize: theme.typography.caption.fontSize,
        margin: 0,
        width: 351,
        overflowX: 'auto',
        padding: theme.spacing(0.5),
      },
    },
    listItem: {
      display: 'block',
      borderTop: 'solid 1px ' + theme.palette.coolGrey[100],
      padding: theme.spacing(0.5, 0),
      '&:last-child': {
        borderBottom: 'solid 1px ' + theme.palette.coolGrey[100],
      },
      '&:only-child': {
        border: 'none',
      },
    },
    tagContainer: {
      display: 'flex',
      alignItems: 'center',
    },
    nameAndChipContainer: {
      display: 'flex',
    },
    tag: {
      color: theme.palette.primary.main,
      width: '14px',
      marginRight: theme.spacing(0.375),
      position: 'relative',
      top: 1,
    },
    moreButton: {
      color: theme.palette.coolGrey[300],
      padding: theme.spacing(0, 2, 0.25),
      '&:hover, &:focus': {
        color: theme.palette.primary.main,
      },
    },
  });
});

export const EntityDrawerPropertyComponent = ({
  menu,
  name,
  deleted,
  originalValue,
  label,
  overridden,
  category,
  edited,
  property,
  propertyFindRelated,
  propertyFindSimilar,
  propertyActions,
  updateEntity,
  handleAction,
  resetPropertyValue,
  isEntityDescribedProperty,
}: EntityDrawerPropertyComponentProps) => {
  const classes = useStyles();
  let content;
  if (isEntityDescribedProperty(property)) {
    if (Array.isArray(property.type)) {
      content = (
        <div>
          {property.value.flat().map((value: any, i: number) => {
            const subProp = { ...property };
            subProp.value = value;
            subProp.type = property.type[i] as EntityDescribedPropertyFormat;
            return (
              <span className={classes.listItem} key={property.name + i}>
                {reducePropertyType(subProp)}
              </span>
            );
          })}
        </div>
      );
    } else {
      content = reducePropertyType(property);
    }
  }
  return (
    <div className={classes.row}>
      <Tooltip
        title={`Original value: ${originalValue}`}
        disableFocusListener={!overridden}
        disableHoverListener={!overridden}
        disableTouchListener={!overridden}
      >
        <Typography className={classes.propertyName}>
          <div className={classes.nameAndChipContainer}>
            {category !== EntityDescribedPropertyCategory.TAG ? (
              name
            ) : (
              <span className={classes.tagContainer}>
                <TagIcon className={classes.tag} /> {name.replace('tag.', '')}
              </span>
            )}
            <span className={classes.chipContainer}>
              {(edited || deleted) && (
                <IndicatorChip
                  label={label}
                  color={deleted ? 'light-red' : 'blue'}
                  className={classes.chip}
                  size="md"
                />
              )}
            </span>
          </div>
        </Typography>
      </Tooltip>
      {edited || propertyFindRelated || propertyFindSimilar ? (
        <>
          <IconButton className={classes.moreButton} onClick={menu.setAnchorEl}>
            <MoreIcon />
          </IconButton>
          <Menu
            id={`${name}-menu`}
            anchorEl={menu.anchorEl}
            open={Boolean(menu.anchorEl)}
            onClose={menu.clearAnchorEl}
            onClick={menu.clearAnchorEl}
          >
            {propertyActions &&
              propertyActions.map(({ name, callback }) => (
                <MenuItem onClick={handleAction(callback)} key={name}>
                  {name}
                </MenuItem>
              ))}
            {propertyFindRelated && (
              <MenuItem onClick={handleAction(handleFindRelated)}>
                Find Related
              </MenuItem>
            )}
            {propertyFindSimilar && (
              <MenuItem onClick={handleAction(handleFindSimilar)}>
                Find Similar
              </MenuItem>
            )}
            {(edited || (deleted && updateEntity !== undefined)) && (
              <MenuItem
                key={'edit-menu-unique-key'}
                onClick={resetPropertyValue}
              >
                Reset Value
              </MenuItem>
            )}
          </Menu>
        </>
      ) : (
        <span />
      )}
      {!deleted && content}
    </div>
  );
};

export default MockHook.withHookHoc(
  EntityDrawerPropertyComponent,
  useEntityDrawerPropertyLogic
);
